import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectBuchungen,
  selectBuchungenStatus,
  selectKategorien,
  loescheNichtSerienBuchungen,
} from 'modules/finanzen/features/finanzenSlice';
import { selectInvestitionen } from 'modules/investitionen/features/investitionenSlice';
import {
  buchungenImMonat,
  sortiereBuchungen,
  summen,
  ausgabenJeKategorie,
  monatLabel,
  verschiebeMonat,
  monatSchluessel,
  istEinnahme,
  betragVon,
  heute,
} from 'modules/finanzen/features/berechnung';
import { kategorieIcon, zahlwegIcon, TYP_AUSGABE } from 'modules/finanzen/features/konstanten';
import { formatKurs, formatDatum } from 'core/utils/format';
import BackToOverview from 'core/components/BackToOverview';
import GesichertGate from 'modules/finanzen/components/GesichertGate';
import KategorieDonut from 'modules/finanzen/components/KategorieDonut';
import ModulTabs from 'modules/finanzen/components/ModulTabs';
import BuchungForm from 'modules/finanzen/components/BuchungForm';
import KategorieManager from 'modules/finanzen/components/KategorieManager';
import ImportDialog from 'modules/finanzen/components/ImportDialog';
import KontoweckerDialog from 'modules/finanzen/components/KontoweckerDialog';

/**
 * Einstieg des Finanzen-Moduls: Monats-Saldo, Ausgaben-Verteilung und die
 * Buchungen des gewaehlten Monats. Nur im gesicherten Modus erreichbar.
 */
export default function FinanzenPage() {
  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12">
      <div className="mb-6">
        <BackToOverview />
      </div>
      <GesichertGate>
        <MonatsAnsicht />
      </GesichertGate>
    </div>
  );
}

/** Entsperrte Ansicht: Monatsnavigation, Saldo-Kopf, Donut und Buchungsliste. */
function MonatsAnsicht() {
  const dispatch = useDispatch();
  const buchungen = useSelector(selectBuchungen);
  const eigeneKategorien = useSelector(selectKategorien);
  const orte = useSelector(selectInvestitionen);
  const status = useSelector(selectBuchungenStatus);
  const [raeumeAuf, setRaeumeAuf] = useState(false);

  const anzahlLoeschbar = useMemo(() => buchungen.filter((b) => !b.serienId).length, [buchungen]);

  // Loescht alle Buchungen ausser den aus Dauerauftraegen erzeugten (nach Rueckfrage).
  const zuruecksetzen = async () => {
    if (anzahlLoeschbar === 0) return;
    const text =
      `${anzahlLoeschbar} Buchung(en) ohne Dauerauftrag-Zuordnung werden gelöscht. ` +
      'Buchungen, die zu einem Dauerauftrag gehören, bleiben erhalten. Fortfahren?';
    if (!window.confirm(text)) return;
    setRaeumeAuf(true);
    try {
      await dispatch(loescheNichtSerienBuchungen()).unwrap();
    } finally {
      setRaeumeAuf(false);
    }
  };

  const ortName = useMemo(() => {
    const map = {};
    orte.forEach((o) => {
      map[o.uuid] = o.institut;
    });
    return map;
  }, [orte]);
  const [monat, setMonat] = useState(monatSchluessel(heute()));
  const [bearbeitung, setBearbeitung] = useState(null);
  const [kategorienOffen, setKategorienOffen] = useState(false);
  const [importOffen, setImportOffen] = useState(false);
  const [kontoweckerOffen, setKontoweckerOffen] = useState(false);
  const [sortFeld, setSortFeld] = useState('datum');
  const [sortRichtung, setSortRichtung] = useState('desc');

  const monatsBuchungen = useMemo(() => buchungenImMonat(buchungen, monat), [buchungen, monat]);
  const sortierteBuchungen = useMemo(
    () => sortiereBuchungen(monatsBuchungen, sortFeld, sortRichtung),
    [monatsBuchungen, sortFeld, sortRichtung]
  );
  const monatsSummen = useMemo(() => summen(monatsBuchungen), [monatsBuchungen]);

  // Aktives Feld erneut waehlen kehrt die Richtung um, sonst neues Feld absteigend.
  const waehleSort = (feld) => {
    if (feld === sortFeld) setSortRichtung((r) => (r === 'asc' ? 'desc' : 'asc'));
    else {
      setSortFeld(feld);
      setSortRichtung('desc');
    }
  };
  const kategorien = useMemo(() => ausgabenJeKategorie(monatsBuchungen), [monatsBuchungen]);

  const neu = () => setBearbeitung({ typ: TYP_AUSGABE, betrag: '', datum: heute() });

  return (
    <>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-ink">📊 Finanzen</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Ende-zu-Ende verschlüsselt · nur du kannst die Beträge lesen.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={zuruecksetzen}
            disabled={anzahlLoeschbar === 0 || raeumeAuf}
            className="rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 disabled:opacity-40"
            title="Alle Buchungen außer Daueraufträgen löschen"
          >
            {raeumeAuf ? 'Lösche…' : 'Zurücksetzen'}
          </button>
          <button
            type="button"
            onClick={() => setKontoweckerOffen(true)}
            className="rounded-lg border border-line px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5"
            title="Ungelesene Sparkasse-Kontowecker-Mails verarbeiten"
          >
            Kontowecker
          </button>
          <button
            type="button"
            onClick={() => setImportOffen(true)}
            className="rounded-lg border border-line px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5"
          >
            Import
          </button>
          <button
            type="button"
            onClick={() => setKategorienOffen(true)}
            className="rounded-lg border border-line px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5"
          >
            Kategorien
          </button>
          <button
            type="button"
            onClick={neu}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500"
          >
            + Erfassen
          </button>
        </div>
      </header>

      <ModulTabs />

      <MonatsNavigation monat={monat} onWechsel={setMonat} />

      {status === 'loading' && buchungen.length === 0 && (
        <p className="text-ink-muted">Lade und entschlüssle…</p>
      )}

      <SaldoKopf summen={monatsSummen} />

      {kategorien.length > 0 && (
        <div className="mb-6 rounded-2xl border border-line bg-paper-soft p-5">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-muted">
            Ausgaben nach Kategorie
          </h2>
          <KategorieDonut kategorien={kategorien} />
        </div>
      )}

      {monatsBuchungen.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-line bg-paper-soft p-8 text-center text-ink-muted">
          Keine Buchungen in diesem Monat. Tippe auf „+ Erfassen“, um deine erste Ausgabe oder Einnahme
          festzuhalten.
        </div>
      ) : (
        <>
          <SortLeiste feld={sortFeld} richtung={sortRichtung} onWaehle={waehleSort} />
          <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-paper">
            {sortierteBuchungen.map((b) => (
              <BuchungZeile
                key={b.uuid}
                buchung={b}
                eigeneKategorien={eigeneKategorien}
                ortName={ortName}
                onClick={() => setBearbeitung(b)}
              />
            ))}
          </ul>
        </>
      )}

      {bearbeitung && <BuchungForm buchung={bearbeitung} onClose={() => setBearbeitung(null)} />}
      {kategorienOffen && <KategorieManager onClose={() => setKategorienOffen(false)} />}
      {importOffen && <ImportDialog onClose={() => setImportOffen(false)} />}
      {kontoweckerOffen && <KontoweckerDialog onClose={() => setKontoweckerOffen(false)} />}
    </>
  );
}

/** Monatsauswahl mit Vor-/Zurück-Pfeilen. */
function MonatsNavigation({ monat, onWechsel }) {
  return (
    <div className="mb-6 flex items-center justify-between rounded-xl border border-line bg-paper px-3 py-2">
      <button
        type="button"
        onClick={() => onWechsel(verschiebeMonat(monat, -1))}
        className="rounded-lg px-3 py-1 text-lg text-ink-muted transition-colors hover:bg-ink/5"
        aria-label="Vorheriger Monat"
      >
        ‹
      </button>
      <span className="font-serif text-lg font-semibold capitalize text-ink">{monatLabel(monat)}</span>
      <button
        type="button"
        onClick={() => onWechsel(verschiebeMonat(monat, 1))}
        className="rounded-lg px-3 py-1 text-lg text-ink-muted transition-colors hover:bg-ink/5"
        aria-label="Nächster Monat"
      >
        ›
      </button>
    </div>
  );
}

/** Sortierleiste ueber der Buchungsliste: nach Datum oder Betrag, Richtung umschaltbar. */
function SortLeiste({ feld, richtung, onWaehle }) {
  const pfeil = richtung === 'asc' ? '↑' : '↓';
  const knopf = (wert, label) => {
    const aktiv = feld === wert;
    return (
      <button
        type="button"
        onClick={() => onWaehle(wert)}
        className={`rounded-lg border px-3 py-1 transition-colors ${
          aktiv ? 'border-brand-600 bg-brand-600/10 text-ink' : 'border-line text-ink-muted hover:bg-ink/5'
        }`}
      >
        {label} {aktiv && <span aria-hidden="true">{pfeil}</span>}
      </button>
    );
  };
  return (
    <div className="mb-2 flex items-center justify-end gap-2 text-xs">
      <span className="text-ink-muted">Sortieren:</span>
      {knopf('datum', 'Datum')}
      {knopf('betrag', 'Betrag')}
    </div>
  );
}

/** Kopf mit den drei Kennzahlen Einnahmen, Ausgaben und Saldo. */
function SaldoKopf({ summen: s }) {
  const saldoTon = s.saldo >= 0 ? 'text-gain' : 'text-loss';
  return (
    <div className="mb-6 grid grid-cols-3 gap-3">
      <Kennzahl label="Einnahmen" wert={formatKurs(s.einnahmen)} ton="text-gain" />
      <Kennzahl label="Ausgaben" wert={formatKurs(s.ausgaben)} ton="text-loss" />
      <Kennzahl label="Saldo" wert={formatKurs(s.saldo)} ton={saldoTon} />
    </div>
  );
}

/** Eine einzelne Kennzahl-Kachel. */
function Kennzahl({ label, wert, ton }) {
  return (
    <div className="rounded-2xl border border-line bg-paper-soft p-4 text-center">
      <p className="text-xs uppercase tracking-wide text-ink-muted">{label}</p>
      <p className={`mt-1 font-serif text-xl font-semibold tabular-nums ${ton}`}>{wert}</p>
    </div>
  );
}

/** Eine Buchungszeile in der Liste; klickbar zum Bearbeiten. */
function BuchungZeile({ buchung, eigeneKategorien, ortName, onClick }) {
  const einnahme = istEinnahme(buchung);
  const vorzeichen = einnahme ? '+' : '−';
  const ton = einnahme ? 'text-gain' : 'text-loss';
  const ort = buchung.ort ? ortName[buchung.ort] : null;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-ink/5"
      >
        <span className="text-xl" aria-hidden="true">
          {kategorieIcon(buchung.typ, buchung.kategorie, eigeneKategorien)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium text-ink">
            {buchung.kategorie}
            {buchung.serienId && <span title="Aus Dauerauftrag"> 🔁</span>}
            {buchung.notiz && <span className="text-ink-muted"> · {buchung.notiz}</span>}
          </span>
          <span className="block text-xs text-ink-muted">
            {formatDatum(buchung.datum)} · {zahlwegIcon(buchung.zahlweg)} {buchung.zahlweg}
            {ort && <span> · 🏦 {ort}</span>}
          </span>
        </span>
        <span className={`shrink-0 font-semibold tabular-nums ${ton}`}>
          {vorzeichen} {formatKurs(betragVon(buchung))}
        </span>
      </button>
    </li>
  );
}
